/**
 * List audit log Model
 * @authour Nihala Dilshi
 *
 */

import React, { useState, useEffect, useMemo } from 'react';
import moment from 'moment-timezone';
import './audit-log.scss';
import { Timeline, Modal, Tabs } from 'antd';
import { useTranslation, Button } from 'soxo-bootstrap-core';
import { DataLogs } from '../../models';

const { TabPane } = Tabs;

export default function AuditLogList({ id, props, record_id, data }) {
    const { t } = useTranslation(); // Translations
    const [auditData, setAuditData] = useState([]); // Store audit logs
    const [auditLogModel, setAuditLogModel] = useState(false); // Modal visibility
    const mode = props?.auditLog?.mode || data;

    /**
     * Fetch audit logs from API
     */
    async function getAuditLog() {
        try {
            const formBody = props?.auditLog ? props : data;
            const result = await DataLogs.getAuditLog(formBody, id);

            if (result.result) {
                setAuditData(result.result); // Update state
                setAuditLogModel(true); // Open modal
            }
        } catch (error) {
            console.error('Error fetching audit logs:', error);
        }
    }

    /**
     * Memoized sorting & grouping of audit data
     */
    const sortedGroupedData = useMemo(() => {
        if (!auditData.length) return [];

        // Group logs by date
        const groupedData = auditData.reduce((acc, log) => {
            const date = log.changed_date;
            if (!acc[date]) acc[date] = [];
            acc[date].push(log);
            return acc;
        }, {});

        // Sort groups by date (descending)
        return Object.entries(groupedData).sort(([dateA], [dateB]) => {
            const formattedA = dateA.split('-').reverse().join('-');
            const formattedB = dateB.split('-').reverse().join('-');
            return new Date(formattedB) - new Date(formattedA);
        });
    }, [auditData]);

    return (
        <>
            {/* Open Modal Button */}
            <div className="paga-actions">
                <Button style={{ borderRadius: '1px' }} type="primary" size="small" onClick={getAuditLog}>
                    {t('AUDIT LOG')}
                </Button>
            </div>

            {/* Audit Log Modal */}
            <Modal
                destroyOnClose
                visible={auditLogModel}
                footer={null}
                onCancel={() => setAuditLogModel(false)}
                // width={"50%"}
                maskClosable={false}
            >
                <Tabs defaultActiveKey="0">
                    <TabPane tab={t('AUDIT LOG')} key="0">
                        <div className="timeline-container">
                            <p className="appointment-id">
                                {mode === 'appointment' ? 'APPOINTMENT ID' : mode === 'registration' ? 'OP NO' : 'ORDER NO'} : {record_id}
                            </p>

                            {sortedGroupedData.length > 0 ? (
                                sortedGroupedData.map(([date, logs]) => (
                                    <div key={date} className="log-group">
                                        <h3 className="log-date">
                                            {moment(date, 'DD-MM-YYYY').isSame(moment(), 'day')
                                                ? 'TODAY'
                                                : moment(date, 'DD-MM-YYYY').format('DD MMM YYYY')}
                                        </h3>

                                        <Timeline>
                                            {logs.map((log, index) => {
                                                let oldValue, newValue;

                                                try {
                                                    const [oldData, newData] = log.changed_data?.split(' --> ')?.map((v) => v?.trim() ?? '');

                                                    // Replace "undefined" / "null" / empty with blank ("—")
                                                    const clean = (v) => (!v || v === 'undefined' || v === 'null' ? '' : v);

                                                    oldValue = clean(oldData);
                                                    newValue = clean(newData);
                                                } catch (error) {
                                                    console.error('Error parsing changed_data:', error);
                                                    oldValue = '';
                                                    newValue = '';
                                                }

                                                return (
                                                    <Timeline.Item key={index} color="gray">
                                                        <div className="log-content">
                                                            <p className="log-details">
                                                                {log.log_details?.split(' ').map((word, i) => (
                                                                    <span key={i} className={i === 0 || i === 2 ? 'highlight' : ''}>
                                                                        {word + ' '}
                                                                    </span>
                                                                ))}
                                                            </p>

                                                            <p className="log-change">
                                                                {oldValue && newValue ? (
                                                                    <>
                                                                        <span className="old-value">{oldValue}</span>
                                                                        <span className="arrow"> → </span>
                                                                        <span className="new-value">{newValue}</span>
                                                                    </>
                                                                ) : !oldValue && newValue ? (
                                                                    <>
                                                                        <span className="arrow"> → </span>
                                                                        <span className="new-value">{newValue}</span>
                                                                    </>
                                                                ) : oldValue && !newValue ? (
                                                                    <>
                                                                        <span className="old-value">{oldValue}</span>
                                                                        <span className="arrow"> → </span>
                                                                        <span className="new-value">—</span>
                                                                    </>
                                                                ) : (
                                                                    <span className="no-change">—</span>
                                                                )}
                                                            </p>
                                                        </div>
                                                    </Timeline.Item>
                                                );
                                            })}
                                        </Timeline>
                                    </div>
                                ))
                            ) : (
                                <div className="no-logs">No audit history available</div>
                            )}
                            {/* 
                                </div>
                            ))} */}
                        </div>
                    </TabPane>
                </Tabs>
            </Modal>
        </>
    );
}
